import { ArrowLeftIcon } from "Icons/ArrowLeft";
import { isEqual } from "lodash";

import { useEffect, useRef, useState } from "react";

import { css } from "@emotion/react";

import { SearchBar } from "components/search/SearchBar";
import { TopNav } from "components/shared/layout/TopNav";
import { Authenticated } from "components/shared/library/Authenticated";
import { Blur } from "components/shared/library/Blur";
import { Button } from "components/shared/library/Button";

import { useAuth } from "contexts/authContext";

import { Job, JobSearch, getJob, useQuerySearchedJobs } from "apis/jobs";

import { useJobSearch, useRecentJobSearches } from "utils/jobsSearch";
import { isViewMobile } from "utils/mediaQueryStrings";

import { white } from "constants/colors";

import { FilterMenuBar } from "./FilterMenuBar";
import { JobDetailSkeleton } from "./JobDetailSkeleton";
import { JobDetailWidget } from "./JobDetailWidget";
import { JobHeaderCard } from "./JobHeaderCard";
import { JobList } from "./JobList";
import { ResumeReviewWidget } from "./ResumeReviewWidget";
import { SignUpWidget } from "./SignUpWidget";

const style = {
  wrapper: css({
    margin: 16,
    width: "-webkit-fill-available",
    display: "flex",
    gap: 16,
  }),
  jobDetails: css({
    display: "flex",
    flexDirection: "column",
    gap: 16,
  }),
  rightSide: css({
    width: "-webkit-fill-available",
    height: "calc(100vh - 165px)",
    overflow: "auto",
  }),
  mobileBack: css({
    background: white,
    width: "-webkit-fill-available",
    padding: "12px 24px 12px 24px",
  }),
};

type Props = {
  job?: Job;
};
export const JobsSearch = ({ job: initialJob }: Props) => {
  const { user } = useAuth();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { jobs, count, loadMoreJobs, isLoadingMore, fetchJobs } =
    useQuerySearchedJobs();
  const { addToRecentSearches } = useRecentJobSearches();
  const [jobDetail, setJobDetail] = useState<Job | undefined>(initialJob);
  const [isDetailsViewOnMobile, setIsDetailsViewOnMobile] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const { jobSearch, upsertJobIdValue } = useJobSearch();

  const prevSearch = useRef<JobSearch>();
  useEffect(() => {
    if (jobSearch && !isEqual(prevSearch.current?.search, jobSearch.search)) {
      setIsLoadingDetails(true);
      fetchJobs(jobSearch);
      addToRecentSearches(jobSearch);
      prevSearch.current = jobSearch;
    }
  }, [jobSearch, addToRecentSearches, fetchJobs]);

  useEffect(() => {
    const get = async () => {
      if (jobSearch.jobId || jobs.length > 0) {
        setJobDetail(await getJob(jobSearch.jobId || jobs[0].jobId));
        setIsLoadingDetails(false);
      }
    };

    get();
  }, [jobs, jobSearch.jobId]);

  // if there is no jobId then set the activeJobId to the first one in the jobs list.
  useEffect(() => {
    if (!jobSearch.jobId && jobs.length) {
      upsertJobIdValue(jobs[0].jobId);
    }
  }, [jobSearch.jobId, jobs, upsertJobIdValue]);

  useEffect(() => {
    if (jobSearch.jobId && !isPageLoaded) {
      // only set detail view on first page load if there is a job id. We do not want to show detail page every time a filter is updated.
      setIsDetailsViewOnMobile(true);
    }
    setIsPageLoaded(true);
  }, [jobSearch.jobId, isPageLoaded]);

  return (
    <TopNav
      pageTitle="Jobs"
      customTopNav={
        <SearchBar isFullWidth searchFilter="Jobs" showSearchIcon />
      }
    >
      {(!isDetailsViewOnMobile || !isViewMobile()) && <FilterMenuBar />}

      {isDetailsViewOnMobile && isViewMobile() && (
        <div css={style.mobileBack}>
          <Button
            background="link"
            IconLeft={ArrowLeftIcon}
            color="black"
            size="small"
            onClick={() => setIsDetailsViewOnMobile(false)}
          >
            Go back to list
          </Button>
        </div>
      )}

      <div css={style.wrapper}>
        {(!isDetailsViewOnMobile || !isViewMobile()) && (
          <JobList
            activeJobId={jobSearch.jobId}
            onPreviewCardClick={() => setIsDetailsViewOnMobile(true)}
            jobs={jobs}
            count={count}
            loadMoreJobs={() => {
              if (!isLoadingDetails) loadMoreJobs();
            }}
            isLoadingMore={isLoadingMore}
          />
        )}
        {(isDetailsViewOnMobile || !isViewMobile()) && (
          <div css={style.rightSide}>
            {jobSearch.jobId && isLoadingDetails && <JobDetailSkeleton />}
            {jobSearch.jobId && jobDetail && !isLoadingDetails && (
              <div css={style.jobDetails}>
                <JobHeaderCard job={jobDetail} />
                {!user && <SignUpWidget />}

                {!user ? (
                  <Blur>
                    <JobDetailWidget
                      description={jobDetail.description}
                      requirements={jobDetail.requirements}
                    />
                  </Blur>
                ) : (
                  <JobDetailWidget
                    description={jobDetail.description}
                    requirements={jobDetail.requirements}
                  />
                )}
                <Authenticated>
                  <ResumeReviewWidget />
                </Authenticated>
              </div>
            )}
          </div>
        )}
      </div>
    </TopNav>
  );
};
